import React from 'react';
import ReactDOM from 'react-dom/client';
import axios from 'axios';
import App from './App';

import './index.scss';
import './styles/theme.css';
import { BrowserRouter } from 'react-router-dom';
import { ConfigProvider } from './context/ConfigContext';
import { AuthProvider } from './context/AuthContext'; // Import AuthProvider
import { ThemeProvider } from './context/ThemeContext'; // light/dark theme
import installDemoAdapter from './demo/installDemoAdapter';
import reportWebVitals from './reportWebVitals';

// Apply the persisted theme to <html> before React renders, so there's no flash
// of the wrong palette on load. ThemeProvider keeps it in sync afterwards.
try {
  const savedTheme = localStorage.getItem('nd-theme');
  document.documentElement.setAttribute('data-theme', savedTheme === 'light' ? 'light' : 'dark');
} catch (_) {
  document.documentElement.setAttribute('data-theme', 'dark');
}

// Attach the logged-in JWT to every request that targets our OWN API, so the
// backend can recognize the session. This is what lets the C&I demo account's
// responses get scrubbed server-side; it is harmless for normal users (endpoints
// that don't require auth simply ignore the token). Scoped to our API base so the
// token is never sent to third-party hosts (e.g. the face API on :5001).
const API_BASE = process.env.REACT_APP_API_BASE_URL || '';

// Demo mode. Installed unconditionally but inert unless the signed-in account
// has is_demo (or ?demo=1 forced it on), in which case the four C&I demo pages
// are served from a curated local dataset instead of the network.
//
// Order against the token interceptor below does not matter: axios runs request
// interceptors in reverse registration order, but the two touch different things
// (this one swaps config.adapter, that one sets a header) and a demo-matched
// request never reaches the network for the header to matter anyway.
installDemoAdapter(axios, API_BASE);

axios.interceptors.request.use((config) => {
  const url = config.url || '';
  const isOurApi = url.startsWith('/') || (API_BASE && url.startsWith(API_BASE));
  // Auth calls (login / forgot-password) must be unauthenticated — never send a
  // cached token, or a stale demo token would get the login itself blocked.
  const isAuthCall = url.includes('/auth/');
  if (isOurApi && !isAuthCall) {
    const token = localStorage.getItem('token');
    if (token && !(config.headers && config.headers.Authorization)) {
      config.headers = config.headers || {};
      config.headers.Authorization = `Bearer ${token}`;
    }
  }
  return config;
});

const root = ReactDOM.createRoot(document.getElementById('root'));
root.render(
  <BrowserRouter>
    <ThemeProvider>
      <AuthProvider>
        <ConfigProvider>
          <App />
        </ConfigProvider>
      </AuthProvider>
    </ThemeProvider>
  </BrowserRouter>
);

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
